import { useState } from 'react';
import { useDispensarMarcasLote, type FiltroDispensaLote } from '@/api/marcas';
import { usePode } from '@/api/me';
import { useTabelasPreco } from '@/api/tabelas-preco';
import { Button } from '@/components/ui/button';
import { ApiError } from '@/lib/api';
import { formatarContagem } from '@/lib/format';

type Tri = '' | 'sim' | 'nao';
const tri = (v: Tri): boolean | undefined => (v === '' ? undefined : v === 'sim');
const numero = (v: string): number | undefined => (v.trim() === '' || Number.isNaN(Number(v.replace(',', '.'))) ? undefined : Number(v.replace(',', '.')));
const CAMPO = 'h-9 rounded-lg border border-border bg-background px-2 text-small';

/**
 * "Dispensar marcas de revisão em lote" (S37-16): filtros -> prévia da contagem (servidor) -> confirmação com o motivo. Só a ferramenta:
 * nada é dispensado sem a confirmação e a contagem confirmada tem de ser a da prévia. Nenhuma faixa nem variação é calculada aqui.
 */
export function DispensaMarcasLote() {
  const { pode } = usePode();
  const tabelas = useTabelasPreco();
  const m = useDispensarMarcasLote();
  const [tabelaId, setTabelaId] = useState('');
  const [min, setMin] = useState('');
  const [max, setMax] = useState('');
  const [minimo, setMinimo] = useState<Tri>('');
  const [meta, setMeta] = useState<Tri>('');
  const [motivo, setMotivo] = useState('');
  const [previa, setPrevia] = useState<{ total: number; restantes: number; naoAvaliadas: number } | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  if (!pode('marca.dispensar')) return null;

  const filtro = (): Omit<FiltroDispensaLote, 'confirmar' | 'esperado'> => ({
    ...(tabelaId ? { tabelaId } : {}),
    ...(numero(min) !== undefined ? { variacaoMinPct: numero(min) as number } : {}),
    ...(numero(max) !== undefined ? { variacaoMaxPct: numero(max) as number } : {}),
    ...(tri(minimo) !== undefined ? { abaixoDoMinimo: tri(minimo) as boolean } : {}),
    ...(tri(meta) !== undefined ? { abaixoDaMeta: tri(meta) as boolean } : {}),
    motivo: motivo.trim() || 'Dispensa em lote',
  });
  const semFiltro = !tabelaId && numero(min) === undefined && numero(max) === undefined && !minimo && !meta;
  const falha = (e: unknown) => setErro(e instanceof ApiError ? e.message : 'falha de comunicação com a API');
  const limpar = () => {
    setPrevia(null);
    setMsg(null);
    setErro(null);
  };

  return (
    <section aria-label="Dispensar marcas de revisão em lote" data-testid="dispensa-marcas-lote" className="flex flex-col gap-3 rounded-2xl border border-border bg-card p-5">
      <div>
        <h2 className="text-title font-semibold">Dispensar marcas “revisar preço” em lote</h2>
        <p className="mt-1 text-small text-ink-secondary">
          Escolha o critério, veja quantas marcas casam e confirme. O preço praticado não muda; a dispensa fica na auditoria com o motivo.
        </p>
      </div>
      <div className="flex flex-wrap items-end gap-3 text-small">
        <label className="flex flex-col gap-1">
          Tabela
          <select className={CAMPO} value={tabelaId} onChange={(e) => { setTabelaId(e.target.value); limpar(); }}>
            <option value="">Todas</option>
            {(tabelas.data ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.codigo}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-1">
          Variação do sugerido de (%)
          <input className={`${CAMPO} w-28`} inputMode="decimal" value={min} onChange={(e) => { setMin(e.target.value); limpar(); }} />
        </label>
        <label className="flex flex-col gap-1">
          até (%)
          <input className={`${CAMPO} w-28`} inputMode="decimal" value={max} onChange={(e) => { setMax(e.target.value); limpar(); }} />
        </label>
        <label className="flex flex-col gap-1">
          Praticado abaixo do mínimo
          <select className={CAMPO} value={minimo} onChange={(e) => { setMinimo(e.target.value as Tri); limpar(); }}>
            <option value="">Tanto faz</option>
            <option value="sim">Sim</option>
            <option value="nao">Não</option>
          </select>
        </label>
        <label className="flex flex-col gap-1">
          Praticado abaixo da meta
          <select className={CAMPO} value={meta} onChange={(e) => { setMeta(e.target.value as Tri); limpar(); }}>
            <option value="">Tanto faz</option>
            <option value="sim">Sim</option>
            <option value="nao">Não</option>
          </select>
        </label>
        <label className="flex min-w-48 flex-1 flex-col gap-1">
          Motivo
          <input className={CAMPO} value={motivo} maxLength={300} onChange={(e) => setMotivo(e.target.value)} />
        </label>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={semFiltro || m.isPending}
          onClick={() => {
            limpar();
            m.mutate({ ...filtro(), confirmar: false }, { onSuccess: (r) => setPrevia({ total: r.total, restantes: r.restantes, naoAvaliadas: r.naoAvaliadas }), onError: falha });
          }}
        >
          Ver prévia
        </Button>
      </div>
      {erro && (
        <p role="alert" className="rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
          {erro}
        </p>
      )}
      {msg && (
        <p role="status" className="text-small text-ink-secondary">
          {msg}
        </p>
      )}
      {previa && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3 text-small text-ink">
          <span data-testid="previa-dispensa">
            {formatarContagem(previa.total)} {previa.total === 1 ? 'marca casa' : 'marcas casam'} com o critério; {formatarContagem(previa.restantes)} ficam abertas.
            {previa.naoAvaliadas > 0 && ` ${formatarContagem(previa.naoAvaliadas)} sem custo ou preço praticado não foram avaliadas.`}
          </span>
          <Button
            type="button"
            size="sm"
            disabled={previa.total === 0 || m.isPending}
            onClick={() =>
              m.mutate(
                { ...filtro(), confirmar: true, esperado: previa.total },
                {
                  onSuccess: (r) => {
                    setPrevia(null);
                    setMsg(`${formatarContagem(r.total)} ${r.total === 1 ? 'marca dispensada' : 'marcas dispensadas'}.`);
                  },
                  onError: falha,
                },
              )
            }
          >
            Dispensar {formatarContagem(previa.total)} {previa.total === 1 ? 'marca' : 'marcas'}
          </Button>
        </div>
      )}
    </section>
  );
}
